import React from "react";
import { Handle, Position, NodeProps } from "reactflow";
import { Box, Typography, Chip } from "@mui/material";
import { ControlNode } from "./types";
import { getNodeDisplayLabel, getNodePreview } from "./utils";

/**
 * Extract variables mentioned in this node
 */
function getNodeVariables(node: ControlNode): string[] {
  const variables: string[] = [];

  switch (node.node_tag) {
    case "declare":
      const declareNode = node as any;
      declareNode.variables?.forEach((v: any) => variables.push(v.name));
      break;
    case "assign_action":
    case "append_action":
      const actionNode = node as any;
      if (actionNode.target?.name) variables.push(actionNode.target.name);
      if (actionNode.source?.name) variables.push(actionNode.source.name);
      break;
    case "map":
      const mapNode = node as any;
      if (mapNode.source_set_var) variables.push(mapNode.source_set_var);
      if (mapNode.iter_var) variables.push(mapNode.iter_var);
      break;
    case "if":
      const ifNode = node as any;
      // Extract variable names from expression (simple regex)
      const matches = ifNode.expr?.match(/\b[a-z_][a-z0-9_]*\b/gi) || [];
      variables.push(...matches.filter((m: string) => !['true', 'false', 'null', 'and', 'or', 'not', 'in'].includes(m.toLowerCase())));
      break;
  }

  return Array.from(new Set(variables)); // Remove duplicates
}

/**
 * Minimal custom node component for React Flow
 * Displays clean, conversation-focused information without technical IDs
 */
const CustomNode: React.FC<NodeProps> = ({ data, selected }) => {
  const node = data as ControlNode & {
    label: string;
    color: string;
    icon: string;
    highlightedVariables?: string[];
    onVariableClick?: (varName: string) => void;
  };

  const displayLabel = getNodeDisplayLabel(node);
  const preview = getNodePreview(node);
  const nodeVariables = getNodeVariables(node);
  const hasHighlightedVars = node.highlightedVariables && node.highlightedVariables.length > 0;

  return (
    <Box
      sx={{
        minWidth: 160,
        maxWidth: 220,
        backgroundColor: "white",
        border: `2px solid ${hasHighlightedVars ? "#FF9800" : selected ? node.color : "#E5E7EB"}`,
        borderRadius: 1.5,
        boxShadow: hasHighlightedVars ? 3 : selected ? 2 : 1,
        transition: "all 0.15s",
        "&:hover": {
          boxShadow: 2,
          borderColor: node.color,
        },
      }}
    >
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: node.color,
          width: 8,
          height: 8,
          border: "1.5px solid white",
        }}
      />

      {/* Node Header */}
      <Box
        sx={{
          backgroundColor: node.color,
          color: "white",
          px: 1.5,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
      >
        <Typography sx={{ fontSize: "1.1rem", lineHeight: 1 }}>
          {node.icon}
        </Typography>
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8rem", lineHeight: 1.2 }}>
          {displayLabel}
        </Typography>
      </Box>

      {/* Node Body - Preview Text */}
      {preview && (
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              lineHeight: 1.3,
              fontSize: "0.7rem",
              wordBreak: "break-word",
            }}
          >
            {preview}
          </Typography>
        </Box>
      )}

      {/* Variable chips */}
      {nodeVariables.length > 0 && (
        <Box sx={{ px: 1.5, pb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {nodeVariables.map((varName) => {
            const isHighlighted = node.highlightedVariables?.includes(varName);
            return (
              <Chip
                key={varName}
                label={varName}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (node.onVariableClick) {
                    node.onVariableClick(varName);
                  }
                }}
                sx={{
                  fontSize: "0.65rem",
                  height: 18,
                  backgroundColor: isHighlighted ? "#FFE0B2" : "#F3F4F6",
                  color: isHighlighted ? "#E65100" : "text.secondary",
                  fontWeight: isHighlighted ? 600 : 400,
                  border: isHighlighted ? "1px solid #FF9800" : "none",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: isHighlighted ? "#FFD699" : "#E5E7EB",
                  },
                }}
              />
            );
          })}
        </Box>
      )}

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: node.color,
          width: 8,
          height: 8,
          border: "1.5px solid white",
        }}
      />
    </Box>
  );
};

export default CustomNode;
